import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { api, type TeamAssignment, type TeamReadings } from '../api/client';
import { AvisoError, AvisoExito } from '../components/Aviso';
import { BarraDeFiltros, Buscador, LimpiarFiltros, Segmentado } from '../components/Filtros';
import { Ficha } from '../components/Ficha';
import { Modal } from '../components/Modal';
import { BarraDeAvance } from '../components/BarraDeAvance';
import { formateadorDeDia } from '../formato';
import { agruparPor, estadoDe, resumir, tonoDe, type EstadoDeLectura } from '../lecturas/resumen';
import { claveVisible, textoBuscable } from '../documentos/clave';
import { AccionIcono } from '../components/AccionIcono';
import { IconoCampana, IconoVer } from '../components/Iconos';

/** Compara sin acentos ni mayúsculas: quien escribe «credito» busca «Crédito». */
const llano = (texto: string) =>
  texto
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase();

const TONO: Record<EstadoDeLectura, string> = {
  LEIDO: 'bueno',
  PENDIENTE: 'aviso',
  VENCIDO: 'critico',
};

type Vista = 'personas' | 'documentos';

/** A quién se le va a recordar, para la ventana de confirmación. */
interface Recordatorio {
  titulo: string;
  cuantos: number;
  cuerpo: { documentId?: string; userIds?: string[] };
}

/**
 * Gerencia: cómo va la gente de mis grupos con la lectura obligatoria.
 *
 * Dos vistas de lo mismo, porque son las dos preguntas que se hace quien da
 * seguimiento: **por persona** («¿quién va atrasado?») y **por documento**
 * («¿qué norma no se ha leído?»). Desde cualquiera se recuerda a quien falta y se
 * baja el reporte.
 *
 * El equipo lo decide el servidor —los grupos de quien mira, sin el general, sin
 * otros gerentes— y no hay manera de pedir el de alguien más.
 */
export function Equipo() {
  const { t, i18n } = useTranslation();
  const [datos, setDatos] = useState<TeamReadings | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [mensaje, setMensaje] = useState<string | null>(null);
  const [vista, setVista] = useState<Vista>('personas');
  const [busca, setBusca] = useState('');
  const [grupo, setGrupo] = useState('');
  const [estado, setEstado] = useState<'' | EstadoDeLectura>('');
  const [persona, setPersona] = useState<string | null>(null);
  const [documento, setDocumento] = useState<string | null>(null);
  const [confirmando, setConfirmando] = useState<Recordatorio | null>(null);
  const [ocupado, setOcupado] = useState(false);

  const dia = formateadorDeDia(i18n.language);

  const cargar = useCallback(async () => {
    try {
      setDatos(await api.teamReadings());
      setError(null);
    } catch (e) {
      setError(e);
    }
  }, []);

  useEffect(() => {
    void cargar();
  }, [cargar]);

  const personas = useMemo(
    () => new Map((datos?.members ?? []).map((m) => [m.userId, m])),
    [datos],
  );

  /** Las lecturas que pasan el filtro de grupo; la búsqueda y el estado se aplican por vista. */
  const delGrupo = useMemo(() => {
    if (!datos) return [];
    return datos.assignments.filter(
      (a) => !grupo || personas.get(a.userId)?.groupIds.includes(grupo),
    );
  }, [datos, grupo, personas]);

  const gente = useMemo(
    () => (datos?.members ?? []).filter((m) => !grupo || m.groupIds.includes(grupo)),
    [datos, grupo],
  );

  const total = resumir(delGrupo);
  const porPersona = agruparPor(delGrupo, (a) => a.userId);
  const porDocumento = agruparPor(delGrupo, (a) => a.documentId);
  const nombreDeGrupo = new Map((datos?.groups ?? []).map((g) => [g.id, g.name]));

  /** ¿Pasa esta fila el filtro de estado? Una persona o un documento «tiene» un estado si alguna lectura lo tiene. */
  const conEstado = (lecturas: readonly TeamAssignment[]) =>
    !estado || lecturas.some((l) => estadoDe(l) === estado);

  const filasPersonas = gente
    .filter((m) => !busca || llano(`${m.fullName} ${m.username}`).includes(llano(busca)))
    .filter((m) => conEstado(porPersona.get(m.userId) ?? []))
    .map((m) => ({ persona: m, resumen: resumir(porPersona.get(m.userId) ?? []) }))
    // Primero quien debe más: es el orden en que se actúa.
    .sort(
      (a, b) =>
        b.resumen.vencidos - a.resumen.vencidos ||
        b.resumen.pendientes - a.resumen.pendientes ||
        a.persona.fullName.localeCompare(b.persona.fullName, i18n.language),
    );

  const filasDocumentos = [...porDocumento.values()]
    .filter((lecturas) => {
      const primera = lecturas[0]!;
      return !busca || llano(textoBuscable(primera)).includes(llano(busca));
    })
    .filter(conEstado)
    .map((lecturas) => ({ documento: lecturas[0]!, resumen: resumir(lecturas) }))
    .sort(
      (a, b) =>
        b.resumen.vencidos - a.resumen.vencidos ||
        b.resumen.pendientes - a.resumen.pendientes ||
        a.documento.title.localeCompare(b.documento.title, i18n.language),
    );

  async function recordar(cuerpo: Recordatorio['cuerpo']) {
    setOcupado(true);
    setError(null);
    setMensaje(null);
    try {
      const resultado = await api.teamRemind(cuerpo);
      setMensaje(
        resultado.failed > 0
          ? t('reading.remindersPartly', { sent: resultado.sent, failed: resultado.failed })
          : t('reading.remindersSent', { count: resultado.sent }),
      );
      setConfirmando(null);
      await cargar();
    } catch (e) {
      setConfirmando(null);
      setError(e);
    } finally {
      setOcupado(false);
    }
  }

  const paginas = (l: TeamAssignment) =>
    l.pageCount ? t('team.pagesOf', { read: l.pagesRead, total: l.pageCount }) : '—';

  const estadoDeLectura = (l: TeamAssignment) => {
    const e = estadoDe(l);
    return (
      <span className={`pildora pildora--${TONO[e]}`}>
        <span className="pildora__punto" aria-hidden="true" />
        {e === 'LEIDO'
          ? t('reading.readOn', { date: dia.format(new Date(l.acknowledgedAt!)) })
          : e === 'VENCIDO'
            ? t('reading.overdueBy', { days: Math.abs(l.daysLeft) })
            : t('reading.daysLeft', { days: l.daysLeft })}
      </span>
    );
  };

  const sinGrupos = datos && datos.groups.length === 0;

  return (
    <>
      <div className="pagina__encabezado">
        <h1 className="pagina__titulo">{t('team.title')}</h1>
        <p className="pagina__bajada">{t('team.subtitle')}</p>
      </div>

      <AvisoError error={error} />
      {mensaje && <AvisoExito>{mensaje}</AvisoExito>}

      {/* Mientras llega, nada de ceros: «0 personas» un instante se lee como
          «no tienes equipo». */}
      {!datos ? (
        !error && <p className="tenue">{t('app.loading')}</p>
      ) : sinGrupos ? (
        <section className="tarjeta">
          <div className="vacio">
            <span className="vacio__titulo">{t('team.noGroups')}</span>
            <span>{t('team.noGroupsHint')}</span>
          </div>
        </section>
      ) : (
        <>
          <section className="fichas" aria-label={t('team.indicators')}>
            <Ficha etiqueta={t('team.people')} valor={gente.length} />
            <Ficha etiqueta={t('team.mandatoryReadings')} valor={total.total} nota={t('team.documentsCount', { count: porDocumento.size })} />
            <Ficha
              etiqueta={t('team.progress')}
              valor={`${Math.round(total.avance * 100)} %`}
              nota={t('team.readOf', { read: total.leidos, total: total.total })}
              tono={total.pendientes > 0 ? 'atencion' : 'normal'}
            />
            <Ficha
              etiqueta={t('reading.overdue')}
              valor={total.vencidos}
              tono={total.vencidos > 0 ? 'atencion' : 'normal'}
            />
          </section>

          <section className="tarjeta">
            <div className="tarjeta__encabezado">
              <h2 className="tarjeta__titulo">{t('team.followUp')}</h2>
              <a className="boton boton--fantasma" href={api.teamExportUrl()}>
                {t('team.export')}
              </a>
              {total.pendientes > 0 && (
                <button
                  type="button"
                  className="boton"
                  disabled={ocupado}
                  onClick={() =>
                    setConfirmando({
                      titulo: t('team.remindAllTitle'),
                      cuantos: total.pendientes,
                      cuerpo: grupo ? { userIds: gente.map((m) => m.userId) } : {},
                    })
                  }
                >
                  {t('team.remindAll', { count: total.pendientes })}
                </button>
              )}
            </div>

            <BarraDeFiltros>
              <Segmentado
                etiqueta={t('team.view')}
                valor={vista}
                onCambio={(valor) => setVista(valor as Vista)}
                opciones={[
                  { valor: 'personas', etiqueta: t('team.byPerson'), conteo: gente.length },
                  { valor: 'documentos', etiqueta: t('team.byDocument'), conteo: porDocumento.size },
                ]}
              />
              <Buscador
                id="buscarEquipo"
                etiqueta={vista === 'personas' ? t('team.searchPerson') : t('team.searchDocument')}
                valor={busca}
                onCambio={setBusca}
              />
              {(datos?.groups.length ?? 0) > 1 && (
                <div className="campo">
                  <label htmlFor="grupoEquipo">{t('documents.group')}</label>
                  <select id="grupoEquipo" value={grupo} onChange={(e) => setGrupo(e.target.value)}>
                    <option value="">{t('team.allMyGroups')}</option>
                    {datos!.groups.map((g) => (
                      <option key={g.id} value={g.id}>
                        {g.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              <Segmentado
                etiqueta={t('reading.status')}
                valor={estado}
                onCambio={(valor) => setEstado(valor as typeof estado)}
                opciones={[
                  { valor: '', etiqueta: t('users.allStatuses') },
                  { valor: 'VENCIDO', etiqueta: t('reading.overdue') },
                  { valor: 'PENDIENTE', etiqueta: t('reading.pending') },
                  { valor: 'LEIDO', etiqueta: t('reading.read') },
                ]}
              />
              <div className="barra-filtros__acciones">
                <LimpiarFiltros
                  hay={Boolean(busca || grupo || estado)}
                  onLimpiar={() => {
                    setBusca('');
                    setGrupo('');
                    setEstado('');
                  }}
                />
              </div>
            </BarraDeFiltros>

            {!datos ? (
              <p className="tenue">{t('app.loading')}</p>
            ) : vista === 'personas' ? (
              filasPersonas.length === 0 ? (
                <div className="vacio">
                  <span className="vacio__titulo">
                    {busca || estado ? t('common.noResults') : t('team.noPeople')}
                  </span>
                </div>
              ) : (
                <div className="tabla-envoltura">
                  <table>
                    <thead>
                      <tr>
                        <th>{t('users.person')}</th>
                        <th>{t('users.groups')}</th>
                        <th>{t('team.progress')}</th>
                        <th>{t('reading.pending')}</th>
                        <th>{t('reading.overdue')}</th>
                        <th />
                      </tr>
                    </thead>
                    <tbody>
                      {filasPersonas.map(({ persona: m, resumen }) => (
                        <tr key={m.userId}>
                          <td>
                            {m.fullName}
                            <div className="campo__ayuda mono">{m.username}</div>
                          </td>
                          <td>
                            {m.groupIds
                              .map((id) => nombreDeGrupo.get(id))
                              .filter(Boolean)
                              .join(', ')}
                          </td>
                          <td>
                            {resumen.total === 0 ? (
                              <span className="tenue">{t('team.nothingMandatory')}</span>
                            ) : (
                              <BarraDeAvance
                                valor={resumen.avance}
                                tono={tonoDe(resumen)}
                                texto={`${resumen.leidos} / ${resumen.total}`}
                                etiqueta={t('team.readOf', { read: resumen.leidos, total: resumen.total })}
                              />
                            )}
                          </td>
                          <td className="mono">{resumen.pendientes || '—'}</td>
                          <td>
                            {resumen.vencidos > 0 ? (
                              <span className="pildora pildora--critico">
                                <span className="pildora__punto" aria-hidden="true" />
                                {resumen.vencidos}
                              </span>
                            ) : (
                              '—'
                            )}
                          </td>
                          <td className="acciones-fila">
                            <AccionIcono
                              etiqueta={t('team.seeDetail')}
                              icono={<IconoVer />}
                              disabled={resumen.total === 0}
                              onClick={() => setPersona(m.userId)}
                            />
                            {resumen.pendientes > 0 && (
                              <AccionIcono
                                etiqueta={t('reading.remind')}
                                icono={<IconoCampana />}
                                disabled={ocupado}
                                onClick={() =>
                                  setConfirmando({
                                    titulo: t('team.remindPersonTitle', { name: m.fullName }),
                                    cuantos: resumen.pendientes,
                                    cuerpo: { userIds: [m.userId] },
                                  })
                                }
                              />
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )
            ) : filasDocumentos.length === 0 ? (
              <div className="vacio">
                <span className="vacio__titulo">
                  {busca || estado ? t('common.noResults') : t('team.noMandatory')}
                </span>
              </div>
            ) : (
              <div className="tabla-envoltura">
                <table>
                  <thead>
                    <tr>
                      <th>{t('documents.name')}</th>
                      <th>{t('documents.version')}</th>
                      <th>{t('reading.deadline')}</th>
                      <th>{t('team.progress')}</th>
                      <th>{t('reading.overdue')}</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {filasDocumentos.map(({ documento: d, resumen }) => (
                      <tr key={d.documentId}>
                        <td>
                          <Link to={`/documentos/${d.documentId}`}>{d.title}</Link>
                          <div className="campo__ayuda mono">{claveVisible(d)}</div>
                        </td>
                        <td className="mono">{d.versionLabel}</td>
                        <td className="mono">{t('reading.daysCount', { days: d.deadlineDays })}</td>
                        <td>
                          <BarraDeAvance
                            valor={resumen.avance}
                            tono={tonoDe(resumen)}
                            texto={`${resumen.leidos} / ${resumen.total}`}
                            etiqueta={t('team.readOf', { read: resumen.leidos, total: resumen.total })}
                          />
                        </td>
                        <td>
                          {resumen.vencidos > 0 ? (
                            <span className="pildora pildora--critico">
                              <span className="pildora__punto" aria-hidden="true" />
                              {resumen.vencidos}
                            </span>
                          ) : (
                            '—'
                          )}
                        </td>
                        <td className="acciones-fila">
                          <AccionIcono
                            etiqueta={t('team.seeDetail')}
                            icono={<IconoVer />}
                            onClick={() => setDocumento(d.documentId)}
                          />
                          {resumen.pendientes > 0 && (
                            <AccionIcono
                              etiqueta={t('reading.remind')}
                              icono={<IconoCampana />}
                              disabled={ocupado}
                              onClick={() =>
                                setConfirmando({
                                  titulo: t('team.remindDocumentTitle', { title: d.title }),
                                  cuantos: resumen.pendientes,
                                  cuerpo: grupo
                                    ? { documentId: d.documentId, userIds: gente.map((m) => m.userId) }
                                    : { documentId: d.documentId },
                                })
                              }
                            />
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <p className="tarjeta__pie">{t('team.note')}</p>
          </section>
        </>
      )}

      {persona && personas.get(persona) && (
        <Modal
          titulo={personas.get(persona)!.fullName}
          onCerrar={() => setPersona(null)}
        >
          <div className="tabla-envoltura">
            <table>
              <thead>
                <tr>
                  <th>{t('documents.name')}</th>
                  <th>{t('reading.dueOn')}</th>
                  <th>{t('reading.status')}</th>
                  <th>{t('team.pagesRead')}</th>
                  <th>{t('reading.lastReminder')}</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {(porPersona.get(persona) ?? []).map((l) => (
                  <tr key={l.documentId}>
                    <td>
                      {l.title}
                      <div className="campo__ayuda mono">{claveVisible(l)}</div>
                    </td>
                    <td className="mono">{dia.format(new Date(l.dueOn))}</td>
                    <td>{estadoDeLectura(l)}</td>
                    <td className="mono">{paginas(l)}</td>
                    <td className="mono">{l.lastRemindedOn ? dia.format(new Date(l.lastRemindedOn)) : '—'}</td>
                    <td className="acciones-fila">
                      {estadoDe(l) !== 'LEIDO' && (
                        <AccionIcono
                          etiqueta={t('reading.remind')}
                          icono={<IconoCampana />}
                          disabled={ocupado}
                          onClick={() => void recordar({ documentId: l.documentId, userIds: [l.userId] })}
                        />
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Modal>
      )}

      {documento && porDocumento.get(documento) && (
        <Modal titulo={porDocumento.get(documento)![0]!.title} onCerrar={() => setDocumento(null)}>
          <div className="tabla-envoltura">
            <table>
              <thead>
                <tr>
                  <th>{t('users.person')}</th>
                  <th>{t('reading.dueOn')}</th>
                  <th>{t('reading.status')}</th>
                  <th>{t('team.pagesRead')}</th>
                  <th>{t('reading.lastReminder')}</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {(porDocumento.get(documento) ?? []).map((l) => (
                  <tr key={l.userId}>
                    <td>{personas.get(l.userId)?.fullName ?? '—'}</td>
                    <td className="mono">{dia.format(new Date(l.dueOn))}</td>
                    <td>{estadoDeLectura(l)}</td>
                    <td className="mono">{paginas(l)}</td>
                    <td className="mono">{l.lastRemindedOn ? dia.format(new Date(l.lastRemindedOn)) : '—'}</td>
                    <td className="acciones-fila">
                      {estadoDe(l) !== 'LEIDO' && (
                        <AccionIcono
                          etiqueta={t('reading.remind')}
                          icono={<IconoCampana />}
                          disabled={ocupado}
                          onClick={() => void recordar({ documentId: l.documentId, userIds: [l.userId] })}
                        />
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Modal>
      )}

      {confirmando && (
        <Modal titulo={confirmando.titulo} onCerrar={() => setConfirmando(null)}>
          <p>{t('team.remindConfirmHint', { count: confirmando.cuantos })}</p>
          <div className="modal__acciones">
            <button type="button" className="boton boton--fantasma" onClick={() => setConfirmando(null)}>
              {t('common.cancel')}
            </button>
            <button
              type="button"
              className="boton"
              disabled={ocupado}
              onClick={() => void recordar(confirmando.cuerpo)}
            >
              {t('reading.remindConfirmAction')}
            </button>
          </div>
        </Modal>
      )}
    </>
  );
}
